import { useCreateTaskTypeMutation } from '@/store/api/services/operations/taskTypes';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

const CreateTaskTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
});

type CreateTaskTypeSchemaType = z.infer<typeof CreateTaskTypeSchema>;

const CreateTaskType = ({ hide }: ModalChildrenProps) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateTaskTypeSchemaType>({
    resolver: zodResolver(CreateTaskTypeSchema),
    mode: 'onSubmit',
  });

  const [createTaskType] = useCreateTaskTypeMutation();

  const onSubmit = async (payload: CreateTaskTypeSchemaType) => {
    try {
      await createTaskType(payload).unwrap();
      toast.success('Task type created successfully');
      hide('create-task-type');
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to create task type';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            {...register('name')}
          />

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description *
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    minRows={4}
                    maxRows={8}
                    placeholder="Provide a detailed description of the task type"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.description
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.description && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('create-task-type')}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Task Type'}
        </Button>
      </DialogActions>
    </>
  );
};

export default CreateTaskType;
